// Meta Pixel: generic, non-health events ONLY, with NO parameters.
//
// Privacy design (see docs/META_PIXEL_PRIVACY.md):
// - Loads only when VITE_META_PIXEL_ID is set. Without it every call is a no-op.
// - fbevents.js never runs in the page itself. It runs inside a hidden frame at the neutral URL
//   /px.html (public/px.html), sandboxed with allow-scripts only (opaque origin, so it cannot read
//   this page's URL, title, DOM, cookies or storage) and referrerpolicy="no-referrer". The page
//   URL Meta receives is always https://curegate.ai/px.html and the referrer is empty. The
//   parent's path (condition pages such as /start/<cancer>), query string (UTMs, fbclid), title
//   and form fields are never visible to the pixel.
// - Automatic events (button clicks, page metadata, form fields) are off: autoConfig=false.
// - Only the event names in PIXEL_EVENTS can be sent, and never with custom data. Per-cancer
//   measurement stays first-party (UTMs + site_visit_events via src/lib/siteVisit.js).
import { adsTagAllowedOnPath, normalizePath, privacySignalOn } from './googleAdsTag';

const PIXEL_ID = String(import.meta.env.VITE_META_PIXEL_ID || '').trim();
const FRAME_SRC = '/px.html';

export const PIXEL_EVENTS = Object.freeze([
  'PageView', 'QuickScreenStart', 'QuickScreenComplete', 'CallbackRequested', 'Lead', 'CompleteRegistration',
]);

// Pages that may load the pixel frame: the SAME general-page allowlist as the Google Ads tag
// (src/lib/googleAdsTag.js: homepage, about/contact, privacy,
// terms), minus the practice referral pages, which never load the pixel. Condition / biomarker pages
// (/start, /start/cancer, /start/<cancer>, /aria, /clinical-trials/*), /upload, /patient-registration, the dashboard, staff and
// portal pages never load it, not even inside the sandboxed frame.
const PIXEL_EXCLUDED_PATHS = Object.freeze(['/refer', '/es/referir']);
export function pixelAllowedOnPath(pathname) {
  return adsTagAllowedOnPath(pathname) && !PIXEL_EXCLUDED_PATHS.includes(normalizePath(pathname));
}

let frame = null;
let frameReady = false;
const pending = [];

function flush() {
  if (!frameReady || !frame?.contentWindow) return;
  // The sandboxed frame has an opaque origin, so targetOrigin must be '*'. Messages carry only
  // the pixel ID and an allowlisted event name.
  frame.contentWindow.postMessage({ type: 'cg-px-init', id: PIXEL_ID }, '*');
  while (pending.length) frame.contentWindow.postMessage({ type: 'cg-px-event', ev: pending.shift() }, '*');
}

let listening = false;

function removeFrame() {
  if (frame) { try { frame.remove(); } catch { /* ignore */ } }
  frame = null;
  frameReady = false;
  pending.length = 0;
}

/**
 * Route-change hook (Routes.jsx). The app is a single-page app, so a frame created on an allowed
 * page would otherwise survive navigation to /upload, /start/<cancer>, etc. Remove it whenever the
 * new path is not allowed (or a privacy signal is on); it is re-created on the next allowed event.
 */
let lastPageViewPath = null;
export function onPixelRouteChange(pathname) {
  if (typeof window === 'undefined') return false;
  if (!pixelAllowedOnPath(pathname) || privacySignalOn()) { removeFrame(); lastPageViewPath = null; return false; }
  // One generic PageView per allowed page (the frame never sees which page).
  const p = normalizePath(pathname);
  if (p === lastPageViewPath) return true;
  lastPageViewPath = p;
  return pixelPageView();
}

function ensureFrame() {
  if (frame) return;
  if (!listening) {
    listening = true;
    window.addEventListener('message', (e) => {
      if (!frame || e.source !== frame.contentWindow) return;
      if (e.data && e.data.type === 'cg-px-ready') { frameReady = true; flush(); }
    });
  }
  createFrame();
}

function createFrame() {
  frame = document.createElement('iframe');
  frame.src = FRAME_SRC;
  frame.title = '';
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.setAttribute('sandbox', 'allow-scripts');
  frame.setAttribute('referrerpolicy', 'no-referrer');
  frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(frame);
}

/** Send one generic event (name only). No-op unless the pixel is configured and the page is an ad-funnel page. */
export function pixelEvent(name) {
  if (!PIXEL_ID || typeof window === 'undefined' || typeof document === 'undefined') return false;
  // Global Privacy Control / Do Not Track: the pixel never loads (same rule as the Google Ads tag).
  if (privacySignalOn()) { removeFrame(); return false; }
  if (!PIXEL_EVENTS.includes(name) || !pixelAllowedOnPath(window.location.pathname)) return false;
  ensureFrame();
  pending.push(name);
  flush();
  return true;
}

export function pixelPageView() { return pixelEvent('PageView'); }
export const pixelLead = () => pixelEvent('Lead');

export const metaPixelConfigured = Boolean(PIXEL_ID);
